import { DailyActivityAggregate, MonthlyActivityAggregate } from "../models/ActivityStats";

/**
 * Pure derivations over the daily activity rollups for the pet profile
 * screen. All "day" values are 'YYYY-MM-DD' device-local day strings as
 * returned by getActivityStats; missing days mean no activity and are
 * treated as zeros.
 */

export interface CatActivitySummary {
    /** Any rollup rows inside the current window. */
    hasActivity: boolean;
    transits: number;
    transitsIn: number;
    transitsOut: number;
    peeks: number;
    denies: number;
    preyAttempts: number;
    /** Percent change in transits vs the previous window, or null when the previous window had none. */
    transitsTrendPct: number | null;
    /** Last day with a prey attempt anywhere in the fetched range (may predate the current window). */
    lastPreyDay: string | null;
    /** Whole days between the last prey attempt and the window end, or null when none in range. */
    daysSinceLastPrey: number | null;
    /** Transit counts by device-local hour over the current window. */
    hourHistogram: number[];
    busiestHour: number | null;
    /**
     * Smallest circular hour window covering >= 80% of transits; may wrap
     * midnight (night-active cats' usually does), so endHourExclusive can be
     * numerically <= startHour. Both are 0-23.
     */
    coreHours: { startHour: number; endHourExclusive: number; pct: number } | null;
    /** Transit counts Monday-first; index 0 = Monday. */
    weekdayCounts: number[];
    /** Monday-first index of the busiest weekday, or null without transits. */
    busiestWeekday: number | null;
    avgTransitsPerDay: number;
    mostActiveDay: { day: string; transits: number } | null;
}

const dayToUtcMs = (day: string) => Date.parse(`${day}T00:00:00Z`);

/** Monday-first weekday index of a 'YYYY-MM-DD' day string. */
export function mondayFirstWeekday(day: string): number {
    return (new Date(dayToUtcMs(day)).getUTCDay() + 6) % 7;
}

const transitsOf = (day: DailyActivityAggregate) => day.transitsIn + day.transitsOut;

function findCoreHours(histogram: number[], threshold = 0.8): CatActivitySummary['coreHours'] {
    const hours = histogram.length;
    const total = histogram.reduce((sum, count) => sum + count, 0);
    if (!total || !hours) {
        return null;
    }

    // Circular: the window may wrap midnight - for night-active cats the
    // core hours usually do, and a linear scan would report a uselessly
    // wide daytime range instead. Shortest window reaching the threshold
    // wins; ties break toward the higher share.
    let best: { startHour: number; length: number; sum: number } | null = null;
    for (let start = 0; start < hours; start++) {
        let sum = 0;
        // length < hours: the whole clock is not a useful "core window".
        for (let length = 1; length < hours; length++) {
            sum += histogram[(start + length - 1) % hours];
            if (sum >= total * threshold) {
                if (!best || length < best.length || (length === best.length && sum > best.sum)) {
                    best = { startHour: start, length, sum };
                }
                break;
            }
        }
    }

    if (!best) {
        return null;
    }

    return {
        startHour: best.startHour,
        endHourExclusive: (best.startHour + best.length) % hours,
        pct: Math.round((best.sum / total) * 100)
    };
}

/**
 * Summarize rollup days for a window. `days` must cover at least the current
 * window and the preceding one of equal length (for the trend), and may
 * extend further back: older rows only feed the last-prey lookback.
 */
export function summarizeCatActivity(
    days: DailyActivityAggregate[],
    windowFromDay: string,
    windowToDay: string
): CatActivitySummary {
    const windowMs = dayToUtcMs(windowToDay) - dayToUtcMs(windowFromDay) + 86_400_000;
    const previousFromMs = dayToUtcMs(windowFromDay) - windowMs;
    const current = days.filter((day) => day.dayLocal >= windowFromDay);
    const previous = days.filter((day) => {
        const dayMs = dayToUtcMs(day.dayLocal);
        return dayMs >= previousFromMs && dayMs < dayToUtcMs(windowFromDay);
    });

    const summary: CatActivitySummary = {
        hasActivity: current.length > 0,
        transits: 0,
        transitsIn: 0,
        transitsOut: 0,
        peeks: 0,
        denies: 0,
        preyAttempts: 0,
        transitsTrendPct: null,
        lastPreyDay: null,
        daysSinceLastPrey: null,
        hourHistogram: new Array(24).fill(0),
        busiestHour: null,
        coreHours: null,
        weekdayCounts: new Array(7).fill(0),
        busiestWeekday: null,
        avgTransitsPerDay: 0,
        mostActiveDay: null
    };

    for (const day of current) {
        summary.transitsIn += day.transitsIn;
        summary.transitsOut += day.transitsOut;
        summary.peeks += day.peeks;
        summary.denies += day.denies;
        summary.preyAttempts += day.preyAttempts;

        for (let hour = 0; hour < summary.hourHistogram.length && hour < day.hourHistogram.length; hour++) {
            summary.hourHistogram[hour] += day.hourHistogram[hour];
        }

        summary.weekdayCounts[mondayFirstWeekday(day.dayLocal)] += transitsOf(day);

        if (!summary.mostActiveDay || transitsOf(day) > summary.mostActiveDay.transits) {
            summary.mostActiveDay = { day: day.dayLocal, transits: transitsOf(day) };
        }
    }

    summary.transits = summary.transitsIn + summary.transitsOut;

    const previousTransits = previous.reduce((sum, day) => sum + transitsOf(day), 0);
    if (previousTransits > 0) {
        summary.transitsTrendPct = Math.round(((summary.transits - previousTransits) / previousTransits) * 100);
    }

    const lastPreyDay = days.filter((day) => day.preyAttempts > 0).map((day) => day.dayLocal).sort().pop() ?? null;
    summary.lastPreyDay = lastPreyDay;
    if (lastPreyDay) {
        summary.daysSinceLastPrey = Math.max(0, Math.round((dayToUtcMs(windowToDay) - dayToUtcMs(lastPreyDay)) / 86_400_000));
    }

    if (summary.transits > 0) {
        summary.busiestHour = summary.hourHistogram.indexOf(Math.max(...summary.hourHistogram));
        summary.busiestWeekday = summary.weekdayCounts.indexOf(Math.max(...summary.weekdayCounts));
        summary.coreHours = findCoreHours(summary.hourHistogram);
    }

    const windowDays = Math.round((dayToUtcMs(windowToDay) - dayToUtcMs(windowFromDay)) / 86_400_000) + 1;
    summary.avgTransitsPerDay = windowDays > 0 ? summary.transits / windowDays : 0;

    return summary;
}

export interface MonthlyTransits {
    /** Device-local calendar month, 'YYYY-MM'. */
    month: string;
    transits: number;
}

/**
 * Transit totals per calendar month from the gateway's groupBy-month
 * aggregates, spanning the earliest returned month through the month of
 * `toDay`. Months without activity have no aggregate row and are
 * zero-filled so the chart keeps a continuous timeline.
 */
export function monthlyTransitCounts(months: MonthlyActivityAggregate[], toDay: string): MonthlyTransits[] {
    if (!months.length) {
        return [];
    }

    const countsByMonth = new Map<string, number>();
    for (const aggregate of months) {
        countsByMonth.set(
            aggregate.monthLocal,
            (countsByMonth.get(aggregate.monthLocal) ?? 0) + aggregate.transitsIn + aggregate.transitsOut
        );
    }

    const firstMonth = [...countsByMonth.keys()].sort()[0];
    const lastMonth = toDay.slice(0, 7);
    const filled: MonthlyTransits[] = [];

    let [year, month] = firstMonth.split('-').map(Number);
    while (filled.length <= 24) {
        const key = `${year}-${String(month).padStart(2, '0')}`;
        filled.push({ month: key, transits: countsByMonth.get(key) ?? 0 });
        if (key >= lastMonth) {
            break;
        }
        month += 1;
        if (month > 12) {
            month = 1;
            year += 1;
        }
    }

    return filled;
}
